import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { toast } from '../../../hooks/use-toast';
import { 
  Upload, Download, Plus, Trash2, Search, Mail, Phone, 
  FileText, Users, CheckCircle, XCircle, AlertCircle, X,
  Filter, Tag, Send, MessageSquare
} from 'lucide-react';
import SMSCreditWarning from '../../../components/SMSCreditWarning';

const ContactsAdmin = () => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [showBlastModal, setShowBlastModal] = useState(false);
  const [blastChannel, setBlastChannel] = useState('email'); // email, sms, both
  const [blastSubject, setBlastSubject] = useState('');
  const [blastMessage, setBlastMessage] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [csvFormat, setCsvFormat] = useState(null);
  const [smsStatus, setSmsStatus] = useState(null);
  const [lastSmsMessage, setLastSmsMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [blastResult, setBlastResult] = useState(null);
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false); // NEW: Track if "Select All" means all pages
  const [totalContacts, setTotalContacts] = useState(0); // NEW: Track total contacts count
  const fileInputRef = useRef(null);
  
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    tags: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchContacts();
    fetchStats();
    fetchCsvFormat();
    fetchSmsStatus();
    // Load last SMS message from localStorage
    const savedLastSms = localStorage.getItem('lastSmsMessage');
    if (savedLastSms) {
      setLastSmsMessage(savedLastSms);
    }
  }, [searchQuery, filterTag, filterSource]);

  const fetchSmsStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contacts/blast/sms/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmsStatus(data);
      }
    } catch (error) {
      console.error('Error fetching SMS status:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterTag) params.append('tag', filterTag);
      if (filterSource) params.append('source', filterSource);
      
      const response = await fetch(`${API_URL}/api/contacts?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Sort by created_at descending (newest first)
      const sortedContacts = (data.contacts || []).sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA;
      });
      setContacts(sortedContacts);
      setTotalContacts(data.total || sortedContacts.length); // Store total count
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contacts/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCsvFormat = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/csv-format`);
      const data = await response.json();
      setCsvFormat(data);
    } catch (error) {
      console.error('Error fetching CSV format:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        title: 'Invalid File',
        description: 'Please upload a CSV file',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/contacts/upload-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        setUploadResult(result);
        fetchContacts();
        fetchStats();
        toast({
          title: 'Upload Complete',
          description: `Imported ${result.imported} contacts`
        });
      } else {
        toast({
          title: 'Upload Failed',
          description: result.detail || 'Failed to upload CSV',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Upload Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newContact.name,
          email: newContact.email || null,
          phone: newContact.phone || null,
          tags: newContact.tags ? newContact.tags.split(',').map(t => t.trim()) : []
        })
      });

      if (response.ok) {
        toast({ title: 'Contact Added' });
        setIsAddDialogOpen(false);
        setNewContact({ name: '', email: '', phone: '', tags: '' });
        fetchContacts();
        fetchStats();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail,
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedContacts.length === 0) return;
    
    if (!window.confirm(`Delete ${selectedContacts.length} contacts?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contacts/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedContacts)
      });

      if (response.ok) {
        toast({ title: `Deleted ${selectedContacts.length} contacts` });
        setSelectedContacts([]);
        fetchContacts();
        fetchStats();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contacts/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Download as CSV file
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({ title: `Exported ${data.count} contacts` });
    } catch (error) {
      toast({
        title: 'Export Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const downloadSampleCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/sample-csv`);
      const data = await response.json();
      
      const blob = new Blob([data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading sample:', error);
    }
  };

  const getSelectedWithPhone = () => {
    return contacts.filter(c => selectedContacts.includes(c.id) && c.phone && c.subscribed_sms);
  };

  const openBlastModal = () => {
    setBlastSubject('Special News from 1st Ave ICP! 🍦');
    setBlastMessage('Hi {{name}},\n\n[Your message here]\n\nThank you for being part of the 1st Ave ICP family!\n\n- 1st Ave ICP Team');
    setBlastResult(null);
    setShowBlastModal(true);
  };

  const sendBlast = async () => {
    if (selectedContacts.length === 0 && !selectAllAcrossPages) {
      toast({ title: 'Error', description: 'Please select contacts first', variant: 'destructive' });
      return;
    }
    if (!blastMessage.trim()) {
      toast({ title: 'Error', description: 'Please enter a message', variant: 'destructive' });
      return;
    }
    if ((blastChannel === 'email' || blastChannel === 'both') && !blastSubject.trim()) {
      toast({ title: 'Error', description: 'Please enter an email subject', variant: 'destructive' });
      return;
    }

    setSending(true);
    setBlastResult(null);

    const results = {};
    const token = localStorage.getItem('token');

    try {
      // Send Email if selected
      if (blastChannel === 'email' || blastChannel === 'both') {
        try {
          const emailRes = await fetch(`${API_URL}/api/contacts/blast/email`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              contact_ids: selectAllAcrossPages ? [] : selectedContacts,
              subject: blastSubject,
              message: blastMessage,
              send_to_all: selectAllAcrossPages
            })
          });
          results.email = await emailRes.json();
        } catch (e) {
          results.email = { success: false, message: e.message };
        }
      }

      // Send SMS if selected
      if (blastChannel === 'sms' || blastChannel === 'both') {
        try {
          const smsRes = await fetch(`${API_URL}/api/contacts/blast/sms`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              contact_ids: selectAllAcrossPages ? [] : selectedContacts,
              message: blastMessage,
              send_to_all: selectAllAcrossPages
            })
          });
          results.sms = await smsRes.json();
        } catch (e) {
          results.sms = { success: false, message: e.message };
        }
      }

      setBlastResult(results);
      
      // Save last sent message for future reference (for SMS)
      if ((blastChannel === 'sms' || blastChannel === 'both') && (results.sms?.status === 'sent' || results.sms?.success)) {
        localStorage.setItem('lastSmsMessage', blastMessage);
        setLastSmsMessage(blastMessage);
      }

      // Show success toast
      const emailSuccess = results.email?.success;
      const smsSuccess = results.sms?.success || results.sms?.status === 'sent';
      
      if (emailSuccess || smsSuccess) {
        toast({ 
          title: '✅ Blast Sent!', 
          description: `${emailSuccess ? `Email: ${results.email.successful} sent. ` : ''}${smsSuccess ? `SMS: ${results.sms.successful} sent.` : ''}`
        });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === contacts.length && selectedContacts.length > 0) {
      // Deselect all
      setSelectedContacts([]);
      setSelectAllAcrossPages(false);
    } else {
      // Select all visible contacts AND enable "select all across pages" if total > visible
      setSelectedContacts(contacts.map(c => c.id));
      // If there are more contacts than currently displayed, set the flag
      if (totalContacts > contacts.length) {
        setSelectAllAcrossPages(true);
      } else {
        setSelectAllAcrossPages(false);
      }
    }
  };

  const toggleSelect = (id) => {
    // When manually toggling individual items, disable "select all across pages"
    setSelectAllAcrossPages(false);
    setSelectedContacts(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const getSourceBadge = (source) => {
    const styles = {
      csv_upload: 'bg-blue-100 text-blue-700',
      contact_form: 'bg-green-100 text-green-700',
      drawing_entry: 'bg-purple-100 text-purple-700',
      manual: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      csv_upload: 'CSV Import',
      contact_form: 'Contact Form',
      drawing_entry: 'Drawing Entry',
      manual: 'Manual'
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded ${styles[source] || styles.manual}`}>
        {labels[source] || source}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black">Contacts</h2>
          <p className="text-gray-600">Manage your email & SMS contact list</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setIsUploadDialogOpen(true)}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Upload className="w-4 h-4" />
            Import CSV
          </Button>
          <Button
            onClick={() => setIsAddDialogOpen(true)}
            className="gap-2 bg-pink-600 hover:bg-pink-700"
          >
            <Plus className="w-4 h-4" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* SMS Credit Warning */}
      <SMSCreditWarning />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 border-2 border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-blue-200">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Mail className="w-4 h-4" />
              <span className="text-sm">With Email</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats.with_email}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-green-200">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <Phone className="w-4 h-4" />
              <span className="text-sm">With Phone</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.with_phone}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-purple-200">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <Mail className="w-4 h-4" />
              <span className="text-sm">Email Subscribed</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{stats.subscribed_email}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-orange-200">
            <div className="flex items-center gap-2 text-orange-600 mb-1">
              <Phone className="w-4 h-4" />
              <span className="text-sm">SMS Subscribed</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{stats.subscribed_sms}</p>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white rounded-xl p-4 border-2 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-2 rounded-lg focus:border-pink-500 focus:outline-none"
          />
        </div>
        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="px-4 py-2 border-2 rounded-lg focus:border-pink-500 focus:outline-none"
        >
          <option value="">All Sources</option>
          <option value="csv_upload">CSV Import</option>
          <option value="contact_form">Contact Form</option>
          <option value="drawing_entry">Drawing Entry</option>
          <option value="manual">Manual</option>
        </select>
        <Button variant="outline" onClick={handleExport} className="gap-2">
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
        {(selectedContacts.length > 0 || selectAllAcrossPages) && (
          <>
            {/* Show banner when Select All across pages is active */}
            {selectAllAcrossPages && totalContacts > contacts.length && (
              <div className="flex items-center gap-2 bg-purple-100 text-purple-800 px-3 py-1.5 rounded-lg text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                All {stats?.subscribed_sms ?? totalContacts} SMS subscribers selected (across all pages)
              </div>
            )}
            <Button 
              onClick={openBlastModal}
              className="gap-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
            >
              <Send className="w-4 h-4" />
              Compose & Send ({selectAllAcrossPages ? `All ${stats?.subscribed_sms ?? totalContacts}` : selectedContacts.length})
            </Button>
            <Button variant="destructive" onClick={handleDeleteSelected} className="gap-2">
              <Trash2 className="w-4 h-4" />
              Delete ({selectedContacts.length})
            </Button>
          </>
        )}
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-xl border-2 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b-2">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={contacts.length > 0 && selectedContacts.length === contacts.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4"
                />
              </th>
              <th className="px-4 py-3 text-left font-bold">Name</th>
              <th className="px-4 py-3 text-left font-bold">Email</th>
              <th className="px-4 py-3 text-left font-bold">Phone</th>
              <th className="px-4 py-3 text-left font-bold">Source</th>
              <th className="px-4 py-3 text-left font-bold">Date Added</th>
              <th className="px-4 py-3 text-left font-bold">Tags</th>
              <th className="px-4 py-3 text-center font-bold">Subscribed</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                  Loading contacts...
                </td>
              </tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                  No contacts found. Import a CSV or add contacts manually.
                </td>
              </tr>
            ) : (
              contacts.map(contact => (
                <tr key={contact.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => toggleSelect(contact.id)}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">{contact.name}</td>
                  <td className="px-4 py-3 text-gray-600">{contact.email || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{contact.phone || '-'}</td>
                  <td className="px-4 py-3">{getSourceBadge(contact.source)}</td>
                  <td className="px-4 py-3 text-gray-600 text-sm">
                    {contact.created_at ? new Date(contact.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    }) : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {contact.tags?.map(tag => (
                        <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-2">
                      {contact.email && (
                        <span title={contact.subscribed_email ? 'Email subscribed' : 'Email unsubscribed'}>
                          <Mail className={`w-4 h-4 ${contact.subscribed_email ? 'text-green-500' : 'text-gray-300'}`} />
                        </span>
                      )}
                      {contact.phone && (
                        <span title={contact.subscribed_sms ? 'SMS subscribed' : 'SMS unsubscribed'}>
                          <Phone className={`w-4 h-4 ${contact.subscribed_sms ? 'text-green-500' : 'text-gray-300'}`} />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* CSV Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black flex items-center gap-2">
              <Upload className="w-6 h-6 text-blue-600" />
              Import Contacts from CSV
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            {/* CSV Format Info */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
              <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                CSV File Requirements
              </h4>
              <ul className="text-sm text-blue-700 space-y-1 ml-7 list-disc">
                <li><strong>First row MUST be column headers</strong></li>
                <li>File must be <strong>.csv</strong> format (UTF-8 encoded)</li>
                <li>Maximum file size: <strong>5MB</strong></li>
                <li>Duplicate emails/phones will be skipped</li>
              </ul>
            </div>

            {/* Column Definitions */}
            <div className="border-2 rounded-xl overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 font-bold text-sm">
                Required & Optional Columns
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Column</th>
                    <th className="px-4 py-2 text-left">Required</th>
                    <th className="px-4 py-2 text-left">Description</th>
                    <th className="px-4 py-2 text-left">Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2 font-mono font-bold">name</td>
                    <td className="px-4 py-2"><CheckCircle className="w-4 h-4 text-green-500" /></td>
                    <td className="px-4 py-2">Contact's full name</td>
                    <td className="px-4 py-2 text-gray-600">John Smith</td>
                  </tr>
                  <tr className="border-t bg-gray-50">
                    <td className="px-4 py-2 font-mono font-bold">email</td>
                    <td className="px-4 py-2"><span className="text-gray-400">Optional</span></td>
                    <td className="px-4 py-2">Email for email campaigns</td>
                    <td className="px-4 py-2 text-gray-600">john@example.com</td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-4 py-2 font-mono font-bold">phone</td>
                    <td className="px-4 py-2"><span className="text-gray-400">Optional</span></td>
                    <td className="px-4 py-2">Phone for SMS campaigns</td>
                    <td className="px-4 py-2 text-gray-600">555-123-4567</td>
                  </tr>
                  <tr className="border-t bg-gray-50">
                    <td className="px-4 py-2 font-mono font-bold">tags</td>
                    <td className="px-4 py-2"><span className="text-gray-400">Optional</span></td>
                    <td className="px-4 py-2">Comma-separated tags</td>
                    <td className="px-4 py-2 text-gray-600">vip,loyalty</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Sample CSV */}
            <div className="bg-gray-900 rounded-xl p-4 text-green-400 font-mono text-sm overflow-x-auto">
              <p className="text-gray-500 mb-2"># Example CSV content:</p>
              <pre>name,email,phone,tags
John Smith,john@example.com,555-123-4567,vip
Jane Doe,jane@example.com,555-987-6543,loyalty
Bob Johnson,,555-456-7890,sms-only</pre>
            </div>

            <Button
              variant="outline"
              onClick={downloadSampleCSV}
              className="w-full gap-2"
            >
              <Download className="w-4 h-4" />
              Download Sample CSV File
            </Button>

            {/* Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-pink-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer block"
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-700">
                  {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-gray-500 mt-1">CSV file up to 5MB</p>
              </label>
            </div>

            {/* Upload Result */}
            {uploadResult && (
              <div className={`rounded-xl p-4 ${uploadResult.imported > 0 ? 'bg-green-50 border-2 border-green-200' : 'bg-yellow-50 border-2 border-yellow-200'}`}>
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  {uploadResult.imported > 0 ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  )}
                  Upload Results
                </h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{uploadResult.imported}</p>
                    <p className="text-sm text-gray-600">Imported</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">{uploadResult.skipped}</p>
                    <p className="text-sm text-gray-600">Skipped</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-600">{uploadResult.total_rows}</p>
                    <p className="text-sm text-gray-600">Total Rows</p>
                  </div>
                </div>
                {uploadResult.errors.length > 0 && (
                  <div className="mt-4 text-sm">
                    <p className="font-medium text-red-600 mb-1">Errors:</p>
                    <ul className="text-red-600 text-xs max-h-32 overflow-y-auto">
                      {uploadResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Plus className="w-6 h-6 text-pink-600" />
              Add Contact
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAddContact} className="space-y-4 pt-4">
            <div>
              <label className="block font-bold text-sm mb-1">Name *</label>
              <input
                type="text"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                required
                className="w-full px-4 py-2 border-2 rounded-lg focus:border-pink-500 focus:outline-none"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block font-bold text-sm mb-1">Email</label>
              <input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                className="w-full px-4 py-2 border-2 rounded-lg focus:border-pink-500 focus:outline-none"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block font-bold text-sm mb-1">Phone</label>
              <input
                type="tel"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                className="w-full px-4 py-2 border-2 rounded-lg focus:border-pink-500 focus:outline-none"
                placeholder="555-123-4567"
              />
            </div>
            <div>
              <label className="block font-bold text-sm mb-1">Tags</label>
              <input
                type="text"
                value={newContact.tags}
                onChange={(e) => setNewContact({ ...newContact, tags: e.target.value })}
                className="w-full px-4 py-2 border-2 rounded-lg focus:border-pink-500 focus:outline-none"
                placeholder="vip, loyalty (comma-separated)"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-pink-600 hover:bg-pink-700">
                Add Contact
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Compose & Send Modal */}
      {showBlastModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black flex items-center gap-2">
                  <Send className="w-6 h-6 text-pink-600" />
                  Compose & Send Message
                </h2>
                <button onClick={() => setShowBlastModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Channel Selection */}
              <div className="mb-6">
                <label className="block text-sm font-bold mb-3">📤 Send Via</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setBlastChannel('email')}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      blastChannel === 'email' 
                        ? 'border-pink-500 bg-pink-50 text-pink-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Mail className="w-6 h-6 mx-auto mb-2" />
                    <span className="font-bold">Email Only</span>
                  </button>
                  <button
                    onClick={() => setBlastChannel('sms')}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      blastChannel === 'sms' 
                        ? 'border-green-500 bg-green-50 text-green-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <MessageSquare className="w-6 h-6 mx-auto mb-2" />
                    <span className="font-bold">SMS Only</span>
                  </button>
                  <button
                    onClick={() => setBlastChannel('both')}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      blastChannel === 'both' 
                        ? 'border-purple-500 bg-purple-50 text-purple-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-center gap-1 mb-2">
                      <Mail className="w-5 h-5" />
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <span className="font-bold">Both</span>
                  </button>
                </div>
              </div>

              {/* Recipients Info */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
                <h4 className="font-bold text-blue-800 mb-2">📬 Recipients</h4>
                <p className="text-blue-700">
                  {selectAllAcrossPages ? (
                    <>
                      <strong>All {blastChannel === 'email' ? (stats?.subscribed_email ?? totalContacts) : (stats?.subscribed_sms ?? totalContacts)}</strong> {blastChannel === 'email' ? 'email subscribers' : 'SMS subscribers'} selected (across all pages)
                    </>
                  ) : (
                    <>
                      <strong>{selectedContacts.length}</strong> contacts selected
                      {(blastChannel === 'sms' || blastChannel === 'both') && (
                        <span> • <strong>{getSelectedWithPhone().length}</strong> have SMS-ready phone numbers</span>
                      )}
                    </>
                  )}
                </p>
              </div>

              {/* SMS Status Info */}
              {(blastChannel === 'sms' || blastChannel === 'both') && smsStatus && (
                <div className={`mb-6 p-4 rounded-xl border-2 ${
                  smsStatus.status === 'ready' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {smsStatus.status === 'ready' ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    )}
                    <div>
                      {smsStatus.status === 'ready' ? (
                        <>
                          <h4 className="font-bold text-green-800">✅ SMS Ready to Send</h4>
                          <p className="text-sm text-green-700 mt-1">
                            Telnyx SMS is configured and ready. Messages will be sent from {smsStatus.phone_number}.
                          </p>
                        </>
                      ) : (
                        <>
                          <h4 className="font-bold text-yellow-800">⏳ SMS Not Configured</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            {smsStatus.message || 'SMS messaging is not configured. Please add Telnyx credentials.'}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Last SMS Message - Quick Copy */}
              {(blastChannel === 'sms' || blastChannel === 'both') && lastSmsMessage && (
                <div className="mb-6 bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-purple-800">📋 Last Sent SMS Message</h4>
                    <button
                      onClick={() => setBlastMessage(lastSmsMessage)}
                      className="px-3 py-1 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 font-medium"
                    >
                      Use This Message
                    </button>
                  </div>
                  <div className="bg-white p-3 rounded-lg border text-sm text-gray-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                    {lastSmsMessage.length > 150 ? lastSmsMessage.substring(0, 150) + '...' : lastSmsMessage}
                  </div>
                </div>
              )}

              {/* Subject Line (Email only) */}
              {(blastChannel === 'email' || blastChannel === 'both') && (
                <div className="mb-4">
                  <label className="block text-sm font-bold mb-2">📧 Email Subject Line</label>
                  <input
                    type="text"
                    value={blastSubject}
                    onChange={(e) => setBlastSubject(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                    placeholder="Enter email subject..."
                  />
                </div>
              )}

              {/* Message Content */}
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">
                  💬 Message Content <span className="font-normal text-gray-500">(Use {"{{name}}"} to personalize)</span>
                </label>
                <textarea
                  value={blastMessage}
                  onChange={(e) => setBlastMessage(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 focus:border-pink-500 focus:outline-none font-mono text-sm"
                  rows="6"
                  placeholder="Enter your message..."
                />
                {(blastChannel === 'sms' || blastChannel === 'both') && (
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{blastMessage.length} characters</span>
                    <span className={blastMessage.length > 160 ? 'text-orange-500' : ''}>
                      {Math.ceil(blastMessage.length / 160) || 1} SMS segment{Math.ceil(blastMessage.length / 160) !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>

              {/* Preview */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h4 className="font-bold mb-2">👀 Preview</h4>
                <div className="bg-white p-3 rounded-lg border text-sm whitespace-pre-wrap">
                  {blastMessage.replace(/\{\{name\}\}/g, 'John Smith')}
                </div>
              </div>

              {/* Result */}
              {blastResult && (
                <div className="mb-6 p-4 rounded-xl bg-gray-50 border-2">
                  <h4 className="font-bold mb-2">📊 Results</h4>
                  {blastResult.email && (
                    <div className={`p-3 rounded-lg mb-2 ${blastResult.email.success ? 'bg-green-50' : 'bg-yellow-50'}`}>
                      <span className="font-bold">Email:</span> {blastResult.email.success ? `✅ ${blastResult.email.successful} sent` : `⚠️ ${blastResult.email.message}`}
                    </div>
                  )}
                  {blastResult.sms && (
                    <div className={`p-3 rounded-lg ${blastResult.sms.success || blastResult.sms.status === 'sent' ? 'bg-green-50' : 'bg-yellow-50'}`}>
                      <span className="font-bold">SMS:</span> {blastResult.sms.success || blastResult.sms.status === 'sent' ? `✅ ${blastResult.sms.successful} sent` : `⚠️ ${blastResult.sms.message}`}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBlastModal(false)}
                  className="flex-1 px-6 py-3 border-2 rounded-full font-bold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={sendBlast}
                  disabled={sending}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-bold hover:from-pink-600 hover:to-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send {blastChannel === 'both' ? 'Email & SMS' : blastChannel === 'sms' ? 'SMS' : 'Email'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsAdmin;
